/**
 * Item parser — mirrors agentic/dev/agents/ASK_FORMAT.md and tools/check_items.py.
 *
 * An item is one markdown file with:
 *   - YAML front-matter (type, state, domain, requires, ask_version, ...)
 *   - a generated status line: `> **Status:** <state> · <label>`
 *   - `## Full item` prose
 *   - `## Compact questions` delimited by `<!-- ask:begin v=N -->` / `<!-- ask:end -->`
 *   - `## Answers` delimited by `<!-- answers:begin -->` / `<!-- answers:end -->`
 *
 * The parser is deliberately tolerant: a malformed item should still render
 * (degraded), never crash the inbox.
 */

export type ItemState =
  | "next:agent"
  | "next:human"
  | "next:external"
  | "next:plan"
  | "next:build"
  | "done:accepted"
  | "done:dropped"
  | "done:superseded";

export type Risk = "low" | "medium" | "high";

/**
 * Orthogonal facets (taxonomy decision D).
 *
 * They carry what the item *kind* cannot: how soon it matters and how big it is.
 * - `urgency`/`effort` are ESTIMATES the agents set on a card's first write;
 *   a console edit always wins (the runner never reassigns an existing value)
 * Neither replaces the ask block or the state — they exist so the inbox can be
 * scanned and filtered on a phone.
 */
export type Urgency = "high" | "normal" | "low";
export type Effort = "S" | "M" | "L";

export const URGENCIES: Urgency[] = ["high", "normal", "low"];
export const EFFORTS: Effort[] = ["S", "M", "L"];

export interface QuestionOption {
  /** Option letter, e.g. "A". */
  key: string;
  /** Short label, e.g. "Single chat". */
  label: string;
  /** Optional description after the label. */
  detail?: string;
  /** Agent's lean. */
  recommended: boolean;
  /**
   * Conversation-contract v2: what changes in the world if this option is
   * chosen (`→ …` lines under the option).
   */
  consequences: string[];
}

export interface Question {
  /** Stable slug. Never renamed. */
  id: string;
  /** Display order number from `Q<n>`. */
  order: number;
  title: string;
  /** Context line shown above the options. */
  context?: string;
  risk: Risk;
  /** Domains/agents blocked until answered. */
  blocks: string[];
  type: "choice" | "free";
  options: QuestionOption[];
}

export interface Answers {
  /** question id -> value (option key or free text). */
  values: Record<string, string>;
  /** Directive to the agents, not a question. */
  nextFocus?: string;
}

/**
 * One runner-recorded change (append-only audit trail at the bottom of the
 * document; runner-owned, never written by a model).
 */
export interface HistoryEntry {
  date?: string;
  /** e.g. "nightly-product". */
  agent?: string;
  body: string;
}

export interface Item {
  path: string;
  filename: string;
  domain: string;
  /** Human-readable title (front-matter `title:`, else the first H1, else the filename). */
  title: string;
  state: ItemState;
  /**
   * True only when the file carries item front-matter (a `state:` key).
   * Long-form reference docs (ONTOLOGY.md, MEMORY_SCHEMA.md, …) are NOT items:
   * they must never appear in the inbox and must never be written back.
   */
  isItem: boolean;
  /** Facet: how soon this matters (defaults to `normal` when absent). */
  urgency: Urgency;
  /** Facet: size estimate, if given. */
  effort?: Effort;
  /** Facet: subcategory within the domain (e.g. legal → consent). */
  subcategory?: string;
  /**
   * Who owns this item (`assignee:`), a person id from agentic/dev/persons.yml.
   * Absent = the default owner (the founder).
   */
  assignee?: string;
  /** Facet: when the item was created (YYYY-MM-DD), stamped by the runner. */
  created?: string;
  /** e.g. "nightly-product" — which agent/runner wrote this file. */
  generatedBy?: string;
  requires?: string;
  description?: string;
  /** Retired in item model v5 — parsed only so foreign/legacy files still render. */
  legacyType?: string;
  askVersion?: number;
  /** Raw front-matter for anything we don't model. */
  frontmatter: Record<string, unknown>;
  /** The prose before the ask block. */
  fullItem: string;
  questions: Question[];
  answers: Answers;
  /** True when the file has an ask block at all. */
  hasAskBlock: boolean;
  /** The runner-recorded audit trail (oldest first; empty when none). */
  history: HistoryEntry[];
  /** Raw file text, needed for write-back. */
  raw: string;
}

const ASK_BEGIN = /<!--\s*ask:begin\b([^>]*)-->/;
const ASK_END = /<!--\s*ask:end\s*-->/;
const ANSWERS_BEGIN = /<!--\s*answers:begin\b[^>]*-->/;
const ANSWERS_END = /<!--\s*answers:end\s*-->/;
const HISTORY_BEGIN = /<!--\s*history:begin\s*-->/;
const HISTORY_END = /<!--\s*history:end\s*-->/;

/**
 * Windows files carry CRLF, and in JavaScript a regex `.` never matches `\r`
 * (it is a line terminator), so `^key: (.*)$` fails on `key: value\r`. Parsing
 * therefore always works on an LF-normalised copy; write-back preserves the
 * original line endings of the file.
 */
export function normalizeEol(text: string): string {
  return text.replace(/\r\n?/g, "\n");
}

/** The dominant end-of-line of a raw file, for write-back. */
function eolOf(text: string): string {
  return text.includes("\r\n") ? "\r\n" : "\n";
}

/** Parse the YAML front-matter. Tolerant: returns {} on failure. */
function parseFrontmatter(text: string): {
  fm: Record<string, unknown>;
  body: string;
} {
  const lf = normalizeEol(text);
  if (!lf.startsWith("---")) return { fm: {}, body: text };
  const end = lf.indexOf("\n---", 3);
  if (end === -1) return { fm: {}, body: text };
  const rawFm = lf.slice(3, end);
  const body = text.slice(text.indexOf("\n---", 3) + 4);
  const fm: Record<string, unknown> = {};
  for (const line of rawFm.split("\n")) {
    const m = /^([A-Za-z_][\w-]*)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const key = m[1];
    let value: unknown = m[2].trim();
    // strip surrounding quotes
    if (
      typeof value === "string" &&
      value.length >= 2 &&
      ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'")))
    ) {
      value = value.slice(1, -1);
    }
    if (value === "true") value = true;
    else if (value === "false") value = false;
    else if (typeof value === "string" && /^\d+$/.test(value)) value = Number(value);
    fm[key] = value;
  }
  return { fm, body };
}

/** Extract the ask block region, or null. */
function extractAskBlock(body: string): string | null {
  const begin = ASK_BEGIN.exec(body);
  if (!begin) return null;
  const afterBegin = body.slice(begin.index + begin[0].length);
  const end = ASK_END.exec(afterBegin);
  return end ? afterBegin.slice(0, end.index) : afterBegin;
}

/** Extract the answers block region, or null. */
function extractAnswersBlock(body: string): string | null {
  const begin = ANSWERS_BEGIN.exec(body);
  if (!begin) return null;
  const afterBegin = body.slice(begin.index + begin[0].length);
  const end = ANSWERS_END.exec(afterBegin);
  return end ? afterBegin.slice(0, end.index) : afterBegin;
}

/** Extract the history block region, or null. */
function extractHistoryBlock(body: string): string | null {
  const begin = HISTORY_BEGIN.exec(body);
  if (!begin) return null;
  const afterBegin = body.slice(begin.index + begin[0].length);
  const end = HISTORY_END.exec(afterBegin);
  return end ? afterBegin.slice(0, end.index) : afterBegin;
}

/** Parse the runner-written history entries (`- date · agent · text`). */
function parseHistory(block: string | null): HistoryEntry[] {
  if (!block) return [];
  const entries: HistoryEntry[] = [];
  for (const raw of block.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = /^-\s*(\d{4}-\d{2}-\d{2})\s*·\s*(\S+)\s*·\s*(.+)$/.exec(line);
    if (m) {
      entries.push({ date: m[1], agent: m[2], body: m[3].trim() });
    } else if (entries.length > 0) {
      // continuation line (e.g. merge provenance) belongs to the entry above
      entries[entries.length - 1].body += ` ${line.replace(/^[-•]\s*/, "")}`;
    }
  }
  return entries;
}

/** Parse the inline metadata suffix of a question heading. */
function parseQuestionMeta(heading: string): {
  order: number;
  title: string;
  id: string;
  risk: Risk;
  blocks: string[];
  type: "choice" | "free";
} {
  // `### Q1 · Chat structure   `id=chat-structure` `risk=low` `blocks=product``
  const orderMatch = /^Q(\d+)\s*[·.\-–]\s*(.*)$/.exec(heading.trim());
  const order = orderMatch ? Number(orderMatch[1]) : 0;
  let title = orderMatch ? orderMatch[2] : heading.trim();

  const idMatch = /`id=([a-z0-9][a-z0-9-]*)`/.exec(title);
  const riskMatch = /`risk=(low|medium|high)`/.exec(title);
  const blocksMatch = /`blocks=([^`]+)`/.exec(title);
  const typeMatch = /`type=(choice|free)`/.exec(title);

  // strip all inline metadata from the visible title
  title = title
    .replace(/`(?:id|risk|blocks|type)=[^`]*`/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return {
    order,
    title,
    id: idMatch ? idMatch[1] : `q${order}`,
    risk: (riskMatch ? riskMatch[1] : "low") as Risk,
    blocks: blocksMatch
      ? blocksMatch[1]
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    type: (typeMatch ? typeMatch[1] : "choice") as "choice" | "free",
  };
}

/** Parse one question block (heading + context + options). */
function parseQuestion(block: string): Question | null {
  const lines = block.split("\n");
  const headingLine = lines.find((l) => l.trim().startsWith("###"));
  if (!headingLine) return null;

  const meta = parseQuestionMeta(headingLine.replace(/^###\s*/, ""));

  const contextLine = lines.find((l) => /^\*\*Context:\*\*/i.test(l.trim()));
  const context = contextLine
    ? contextLine.replace(/^\s*\*\*Context:\*\*\s*/i, "").trim()
    : undefined;

  /** Option lines: `- [ ] **A** Label` plus their `→ consequence` lines. */
  const options: QuestionOption[] = [];
  for (let i = 0; i < lines.length; i++) {
    // - [ ] **A** Single chat *(recommended)*
    const m = /^\s*-\s*\[[ xX]\]\s*\*\*([A-Z0-9]+)\*\*\s*(.*)$/.exec(lines[i]);
    if (!m) continue;
    let rest = m[2].trim();
    const recommended = /\*\(recommended\)\*/i.test(rest);
    rest = rest.replace(/\*\(recommended\)\*/gi, "").trim();
    // Split "Label — detail" or "Label: detail"
    const sep = /^(.*?)\s*[—–:]\s+(.*)$/.exec(rest);
    // Consequence lines belong to the option above them (contract v2).
    const consequences: string[] = [];
    for (let j = i + 1; j < lines.length; j++) {
      const c = /^\s*(?:>\s*)?→\s*(.+)$/.exec(lines[j]);
      if (!c) break;
      consequences.push(c[1].trim());
      i = j;
    }
    options.push({
      key: m[1],
      label: sep ? sep[1].trim() : rest,
      detail: sep ? sep[2].trim() : undefined,
      recommended,
      consequences,
    });
  }

  return {
    id: meta.id,
    order: meta.order,
    title: meta.title,
    context,
    risk: meta.risk,
    blocks: meta.blocks,
    type: meta.type,
    options,
  };
}

/** Parse the answers block into a map. */
function parseAnswers(block: string | null): Answers {
  const answers: Answers = { values: {} };
  if (!block) return answers;
  for (const line of block.split("\n")) {
    // `_` is allowed so reserved keys such as `next_focus` round-trip.
    const m = /^\s*-\s*([a-z0-9][a-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const id = m[1];
    let value = m[2].trim();
    if (
      value.length >= 2 &&
      ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'")))
    ) {
      value = value.slice(1, -1);
    }
    if (id === "next_focus") answers.nextFocus = value;
    else answers.values[id] = value;
  }
  return answers;
}

/**
 * Raw `- id: value` lines of the existing answers block, so lines we do not
 * model (answers to questions that were renamed/removed) can be carried over
 * verbatim instead of being deleted on the next save (ASK_FORMAT §5.4).
 */
function rawAnswerLines(raw: string): { id: string; line: string }[] {
  const lf = normalizeEol(raw);
  const begin = ANSWERS_BEGIN.exec(lf);
  if (!begin) return [];
  const afterBegin = lf.slice(begin.index + begin[0].length);
  const end = ANSWERS_END.exec(afterBegin);
  const block = end ? afterBegin.slice(0, end.index) : afterBegin;
  const out: { id: string; line: string }[] = [];
  for (const line of block.split("\n")) {
    const m = /^\s*-\s*([a-z0-9][a-z0-9_-]*)\s*:/.exec(line);
    if (m) out.push({ id: m[1], line: line.trim() });
  }
  return out;
}

/** Parse a full item file. */
export function parseItem(path: string, raw: string): Item {
  // Parse on an LF-normalised copy (Windows CRLF breaks `.*$` regexes); the
  // returned `raw` keeps the original bytes so write-back is lossless.
  const text = normalizeEol(raw);
  const { fm, body } = parseFrontmatter(text);
  const filename = path.split("/").pop() ?? path;

  const askBlock = extractAskBlock(body);
  const answersBlock = extractAnswersBlock(body);
  const historyBlock = extractHistoryBlock(body);

  // Questions: split the ask block on `### ` headings.
  let questions: Question[] = [];
  if (askBlock) {
    const chunks = askBlock
      .split(/\n(?=###\s)/)
      .map((c) => c.trim())
      .filter((c) => c.startsWith("###"));
    questions = chunks
      .map(parseQuestion)
      .filter((q): q is Question => q !== null)
      .sort((a, b) => a.order - b.order);
  }

  // Full item prose: everything before the ask block (minus the status line).
  let fullItem = body;
  const askBegin = ASK_BEGIN.exec(body);
  if (askBegin) fullItem = body.slice(0, askBegin.index);
  fullItem = fullItem
    .replace(/^>\s*\*\*Status:\*\*[^\n]*$/m, "")
    .replace(/^##\s*Compact questions\s*$/m, "")
    .trim();
  // The history block is a record, not prose — keep it out of the item body.
  const historyIdx = fullItem.search(/^##\s+History\s*$/m);
  if (historyIdx !== -1) fullItem = fullItem.slice(0, historyIdx).trim();

  const askVersionMatch = askBegin ? /v=(\d+)/.exec(askBegin[1]) : null;

  const firstHeading = body
    .split("\n")
    .find((l) => l.trim().startsWith("# "))
    ?.replace(/^#\s*/, "")
    .trim();

  return {
    path,
    filename,
    domain: String(fm.domain ?? "unknown"),
    title:
      (typeof fm.title === "string" && fm.title.trim()) ||
      (typeof fm.description === "string" && fm.description.trim()) ||
      firstHeading ||
      filename,
    state: (fm.state as ItemState) ?? "next:human",
    isItem: typeof fm.state === "string" && fm.state.trim() !== "",
    urgency: parseUrgency(fm.urgency),
    effort: parseEffort(fm.effort),
    subcategory: typeof fm.subcategory === "string" ? fm.subcategory.trim() : undefined,
    assignee: typeof fm.assignee === "string" && fm.assignee.trim() ? fm.assignee.trim() : undefined,
    created: typeof fm.created === "string" ? fm.created : undefined,
    generatedBy:
      typeof fm.generated_by === "string" && fm.generated_by.trim()
        ? fm.generated_by.trim()
        : undefined,
    requires: fm.requires ? String(fm.requires) : undefined,
    description: fm.description ? String(fm.description) : undefined,
    legacyType: fm.type ? String(fm.type) : undefined,
    askVersion: askVersionMatch
      ? Number(askVersionMatch[1])
      : fm.ask_version
        ? Number(fm.ask_version)
        : undefined,
    frontmatter: fm,
    fullItem,
    questions,
    answers: parseAnswers(answersBlock),
    hasAskBlock: askBlock !== null,
    history: parseHistory(historyBlock),
    raw,
  };
}

/** True when the item is waiting on the founder. */
export function isWaitingOnHuman(item: Item): boolean {
  return item.state === "next:human";
}

/**
 * Housekeeping items are the housekeeping agents' own proposals — consolidation
 * plans (nightly-consolidator) and reviews (nightly-reviewer). They share one
 * action vocabulary, get their own tab, and are kept OUT of the regular domain
 * tabs so ordinary domain work is never hidden behind a 5-item plan.
 */
export function isHousekeeping(item: Item): boolean {
  return /nightly-(consolidator|reviewer)\b/.test(item.generatedBy ?? "");
}

/** How many questions are still unanswered. */
export function unansweredCount(item: Item): number {
  return item.questions.filter((q) => !(q.id in item.answers.values)).length;
}

/** Tolerant facet parsing: an unknown/missing value falls back to the default. */
export function parseUrgency(value: unknown): Urgency {
  const v = typeof value === "string" ? value.trim().toLowerCase() : "";
  return (URGENCIES as string[]).includes(v) ? (v as Urgency) : "normal";
}

export function parseEffort(value: unknown): Effort | undefined {
  const v = typeof value === "string" ? value.trim().toUpperCase() : "";
  return (EFFORTS as string[]).includes(v) ? (v as Effort) : undefined;
}

/**
 * Set `urgency:` / `effort:` in the front-matter, changing nothing else
 * (same contract as `serializeState`: a surgical line edit, never a rewrite).
 */
export function serializeFacets(
  item: Item,
  facets: { urgency?: Urgency; effort?: Effort },
): string {
  let out = item.raw;
  if (facets.urgency !== undefined) out = upsertFrontmatterLine(out, "urgency", facets.urgency);
  if (facets.effort !== undefined) out = upsertFrontmatterLine(out, "effort", facets.effort);
  return out;
}

/** Replace a key in the YAML front-matter, or insert it after `state:`. */
function upsertFrontmatterLine(raw: string, key: string, value: string): string {
  const restored = withOriginalEol(raw, (lf) => {
    if (!lf.startsWith("---")) return lf;
    const end = lf.indexOf("\n---", 3);
    if (end === -1) return lf;
    const fm = lf.slice(0, end);
    const rest = lf.slice(end);
    const line = `${key}: ${value}`;
    const keyRe = new RegExp(`^${key}:.*$`, "m");
    if (keyRe.test(fm)) return fm.replace(keyRe, line) + rest;
    const stateRe = /^state:.*$/m;
    const patched = stateRe.test(fm)
      ? fm.replace(stateRe, (m) => `${m}\n${line}`)
      : `${fm}\n${line}`;
    return patched + rest;
  });
  return restored;
}

/**
 * Set or clear the `assignee:` front-matter field — a surgical line edit.
 * A string assigns to that person; `null` removes the line (default owner).
 * Everything else in the file is preserved byte-for-byte.
 */
export function serializeAssignee(item: Item, id: string | null): string {
  if (id !== null) return upsertFrontmatterLine(item.raw, "assignee", id);
  return withOriginalEol(item.raw, (lf) => {
    if (!lf.startsWith("---")) return lf;
    const end = lf.indexOf("\n---", 3);
    if (end === -1) return lf;
    const fm = lf.slice(0, end);
    const rest = lf.slice(end);
    return fm.replace(/^assignee:.*\n?/m, "") + rest;
  });
}

/**
 * Apply a patch to an LF-normalised copy of `raw`, then restore the file's
 * original line endings. Every write path goes through this: files are edited
 * on Windows and in CI, and mixed line endings must never leak into a commit.
 */
function withOriginalEol(raw: string, patch: (lf: string) => string): string {
  const eol = eolOf(raw);
  const out = patch(normalizeEol(raw));
  return eol === "\r\n" ? out.replace(/\n/g, "\r\n") : out;
}

/** True when the item is answerable on a phone (no high-risk questions). */
export function isMobileAnswerable(item: Item): boolean {
  if (!item.hasAskBlock) return false;
  return item.questions.every((q) => q.risk !== "high");
}

/**
 * Serialize answers back into the answers block, preserving the surrounding
 * file exactly. This is the ONLY write path — it never touches the ask block.
 */
export function serializeAnswers(item: Item, answers: Answers): string {
  const lines: string[] = [];
  const emitted = new Set<string>();
  for (const q of item.questions) {
    const value = answers.values[q.id];
    if (value === undefined || value === "") continue;
    // Bare option letters stay bare; anything else is the human's own wording
    // (contract v2 lets every question be answered in free text).
    const isOptionKey = q.options.some((o) => o.key === value);
    lines.push(isOptionKey ? `- ${q.id}: ${value}` : `- ${q.id}: "${value.replace(/"/g, '\\"')}"`);
    emitted.add(q.id);
  }
  if (answers.nextFocus) {
    lines.push(`- next_focus: "${answers.nextFocus.replace(/"/g, '\\"')}"`);
  }
  // Never delete an answer we do not model: carry over lines whose question is
  // no longer part of the ask block (renamed/removed), verbatim.
  const known = new Set(item.questions.map((q) => q.id));
  for (const { id, line } of rawAnswerLines(item.raw)) {
    if (known.has(id) || id === "next_focus") continue;
    if (emitted.has(id)) continue;
    lines.push(line);
    emitted.add(id);
  }
  const block = `<!-- answers:begin -->\n${lines.join("\n")}\n<!-- answers:end -->`;

  return withOriginalEol(item.raw, (lf) => {
    const begin = ANSWERS_BEGIN.exec(lf);
    if (begin) {
      const afterBegin = lf.slice(begin.index + begin[0].length);
      const end = ANSWERS_END.exec(afterBegin);
      if (end) {
        const start = begin.index;
        const stop = begin.index + begin[0].length + end.index + end[0].length;
        return lf.slice(0, start) + block + lf.slice(stop);
      }
    }
    // No answers block yet: append one.
    return `${lf.trimEnd()}\n\n## Answers\n\n${block}\n`;
  });
}

/** Replace the front-matter `state:` value, preserving everything else. */
export function serializeState(item: Item, state: ItemState): string {
  const label = STATE_LABELS[state] ?? state;
  return withOriginalEol(item.raw, (lf) => {
    let out = lf.replace(/^state:.*$/m, `state: ${state}`);
    out = out.replace(/^>\s*\*\*Status:\*\*.*$/m, `> **Status:** ${state} · ${label}`);
    return out;
  });
}

export const STATE_LABELS: Record<string, string> = {
  "next:agent": "agent owes work",
  "next:human": "waiting on founder",
  "next:external": "blocked externally",
  "next:plan": "released for planning",
  "next:build": "plan reviewed — execute in VS Code",
  "next:implement": "released for implementation", // legacy files only
  "done:accepted": "approved",
  "done:dropped": "dropped",
  "done:superseded": "superseded",
};
